import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { userService, IUser } from '../../../../services/user/UserService';
import { parseApiError } from '../../../../utils/parseApiError';
import { useMergeAccountsMutation } from '../../../../hooks/useDashboardQueries';
import { formatUAHGrouped } from '../../../../shared/utils/currency';
import { DashboardModal } from './DashboardModal';
import { ChangeList, FieldChange } from './ConfirmChangeDialog';

interface MergeModalProps {
  survivorId: string;
  survivorEmail: string;
  survivorPhone: string;
  survivorBalance: number; // already in ₴ (UserDetails divides cents by 100)
  onClose: () => void;
}

export const MergeModal = memo(function MergeModal({
  survivorId,
  survivorEmail,
  survivorPhone,
  survivorBalance,
  onClose,
}: MergeModalProps) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [candidate, setCandidate] = useState<IUser | null>(null);
  const [candidates, setCandidates] = useState<IUser[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [confirming, setConfirming] = useState(false);
  const mutation = useMergeAccountsMutation(survivorId);

  const handleSearch = useCallback(async () => {
    const q = term.trim();
    if (!q) return;
    setSearching(true);
    setSearchError(null);
    setCandidate(null);
    setCandidates([]);
    setConfirming(false);
    try {
      const looksLikeEmail = q.includes('@');
      const res = await userService.getUsers(
        looksLikeEmail ? { email: q } : { phone: q.replace(/[^\d+]/g, '') },
        1,
        5,
        null,
      );
      const matches = res.data.filter((u) => u._id !== survivorId);
      if (matches.length === 0) {
        setSearchError(t('dashboard.userDetails.mergeNoResults'));
      } else if (matches.length === 1) {
        setCandidates(matches);
        setCandidate(matches[0]);
      } else {
        setCandidates(matches);
      }
    } catch {
      setSearchError(t('dashboard.userDetails.mergeFailed'));
    } finally {
      setSearching(false);
    }
  }, [term, survivorId, t]);

  const combinedBalance = candidate
    ? survivorBalance + (candidate.balance ?? 0) / 100
    : survivorBalance;

  const confirmReady =
    !!candidate &&
    confirmText.trim().toUpperCase() === t('dashboard.userDetails.mergeConfirmWord');

  const handleSubmit = useCallback(() => {
    if (!candidate || !confirmReady) return;
    if (!confirming) {
      setConfirming(true);
      return;
    }
    mutation.mutate(candidate._id, { onSuccess: onClose });
  }, [candidate, confirmReady, confirming, mutation, onClose]);

  const none = t('dashboard.userDetails.confirm.none');
  const candidateLabel = candidate ? candidate.email || candidate.phone || candidate._id : '';
  const mergeChanges: FieldChange[] = candidate
    ? [
        {
          label: t('dashboard.userDetails.mergeSelected'),
          from: candidateLabel,
          to: t('dashboard.userDetails.confirm.closed'),
        },
        {
          label: t('dashboard.userDetails.balance'),
          from: formatUAHGrouped(survivorBalance),
          to: formatUAHGrouped(combinedBalance),
        },
        ...(!survivorEmail && candidate.email
          ? [{ label: t('dashboard.userDetails.confirm.email'), from: none, to: candidate.email }]
          : []),
        ...(!survivorPhone && candidate.phone
          ? [{ label: t('dashboard.userDetails.confirm.phone'), from: none, to: candidate.phone }]
          : []),
      ]
    : [];

  return (
    <DashboardModal
      titleId="merge-modal-title"
      icon="merge"
      title={t('dashboard.userDetails.mergeTitle')}
      subtitle={t('dashboard.userDetails.mergeSubtitle')}
      onClose={onClose}
      error={
        mutation.isError
          ? parseApiError(mutation.error, t('dashboard.userDetails.mergeFailed')).message
          : null
      }
      cancelLabel={confirming ? t('dashboard.userDetails.confirm.back') : t('dashboard.userDetails.cancel')}
      onCancel={confirming ? () => setConfirming(false) : undefined}
      submitLabel={confirming ? t('dashboard.userDetails.confirm.confirm') : t('dashboard.userDetails.mergeConfirm')}
      submittingLabel={t('dashboard.userDetails.saving')}
      onSubmit={handleSubmit}
      isSubmitting={mutation.isPending}
      submitDisabled={!confirmReady}
    >
      {confirming && candidate ? (
        <div>
          <p className="ud-confirm-message">
            {t('dashboard.userDetails.confirm.mergeMessage', { absorbed: candidateLabel })}
          </p>
          <ChangeList changes={mergeChanges} />
          <p className="ud-confirm-note">{t('dashboard.userDetails.mergeWarning')}</p>
        </div>
      ) : (
        <>
          <div>
            <label htmlFor="merge-search" className="balance-field-label">
              {t('dashboard.userDetails.mergeSearchLabel')}
            </label>
            <div className="balance-amount-input-wrap">
              <input
                id="merge-search"
                className="balance-amount-input"
                type="text"
                autoComplete="off"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={t('dashboard.userDetails.mergeSearchPlaceholder')}
              />
            </div>
            <button
              type="button"
              className="balance-btn balance-btn-cancel"
              onClick={handleSearch}
              disabled={searching || term.trim() === ''}
              style={{ marginTop: '0.5rem' }}
            >
              {t('dashboard.userDetails.mergeSearch')}
            </button>
          </div>

          {searchError && (
            <div className="balance-error" role="alert">
              <span className="material-symbols-outlined" aria-hidden="true">error</span>
              <span>{searchError}</span>
            </div>
          )}

          {candidates.length > 1 && !candidate && (
            <div>
              <label className="balance-field-label">
                {t('dashboard.userDetails.mergeMultipleResults')}
              </label>
              {candidates.map((u) => (
                <button
                  key={u._id}
                  type="button"
                  className="balance-btn balance-btn-cancel"
                  onClick={() => setCandidate(u)}
                  style={{ width: '100%', marginTop: '0.5rem', justifyContent: 'flex-start' }}
                >
                  {u.email || u.phone || u._id}
                </button>
              ))}
            </div>
          )}

          {candidate && (
            <>
              <div className="balance-modal-current">
                <span>{t('dashboard.userDetails.mergeSelected')}</span>
                <strong>{candidate.email || candidate.phone || candidate._id}</strong>
              </div>

              {candidates.length > 1 && (
                <button
                  type="button"
                  className="balance-btn balance-btn-cancel"
                  onClick={() => setCandidate(null)}
                  style={{ marginTop: '0.5rem' }}
                >
                  {t('dashboard.userDetails.mergeChangeSelection')}
                </button>
              )}

              <div id="merge-preview" className="balance-preview" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.25rem' }}>
                <span>{t('dashboard.userDetails.mergePreviewTitle')}</span>
                {!survivorEmail && candidate.email && (
                  <span>• {t('dashboard.userDetails.mergeGainsEmail', { email: candidate.email })}</span>
                )}
                {!survivorPhone && candidate.phone && (
                  <span>• {t('dashboard.userDetails.mergeGainsPhone', { phone: candidate.phone })}</span>
                )}
                <span>• {t('dashboard.userDetails.mergeCombinedBalance', { balance: formatUAHGrouped(combinedBalance) })}</span>
              </div>

              <div className="balance-error" role="note" style={{ background: '#fff7ed', color: '#9a3412' }}>
                <span className="material-symbols-outlined" aria-hidden="true">warning</span>
                <span>{t('dashboard.userDetails.mergeWarning')}</span>
              </div>

              <div>
                <label htmlFor="merge-confirm" className="balance-field-label">
                  {t('dashboard.userDetails.mergeConfirmLabel')}
                </label>
                <div className="balance-amount-input-wrap">
                  <input
                    id="merge-confirm"
                    className="balance-amount-input"
                    type="text"
                    autoComplete="off"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={t('dashboard.userDetails.mergeConfirmWord')}
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </DashboardModal>
  );
});

export default MergeModal;
